import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Route, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Search,
  Zap, ArrowRight, AlertTriangle, Target, GitBranch, ToggleLeft, ToggleRight,
  Copy, Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const CONDITION_FIELDS = [
  { value: "source", label: "Lead Source" },
  { value: "campaign", label: "Campaign" },
  { value: "project", label: "Project" },
  { value: "budget_min", label: "Budget (Min)" },
  { value: "budget_max", label: "Budget (Max)" },
  { value: "budget", label: "Budget" },
  { value: "locality", label: "Locality" },
  { value: "score", label: "Lead Score" },
  { value: "configuration", label: "Configuration" },
  { value: "platform", label: "Platform" },
];

const OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "gt", label: "Greater Than" },
  { value: "lt", label: "Less Than" },
  { value: "gte", label: ">= " },
  { value: "lte", label: "<=" },
  { value: "in", label: "In List" },
];

const ASSIGNMENT_METHODS = [
  { value: "round_robin", label: "Round Robin", desc: "Evenly distribute among team members" },
  { value: "weighted", label: "Weighted", desc: "Distribute based on priority weights" },
  { value: "fixed_user", label: "Fixed User", desc: "Always assign to a specific user" },
  { value: "availability", label: "Availability Based", desc: "Assign to first available member" },
  { value: "least_loaded", label: "Least Loaded", desc: "Assign to member with fewest active leads" },
];

const ACTION_TYPES = [
  { value: "create_task", label: "Create Task" },
  { value: "add_tag", label: "Add Tag" },
  { value: "notify_manager", label: "Notify Manager" },
  { value: "set_stage", label: "Set Stage" },
];

const STAGES = ["new", "contacted", "qualified", "site_visit_scheduled", "site_visit", "revisit", "negotiation_scheduled", "negotiation", "booked", "re_engaged", "lost"];
const ASSIGN_PRIORITIES = [
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
];

const emptyCondition = { field: "source", operator: "equals", value: "" };
const emptyAction = { action_type: "create_task", value: "" };
const emptyEscalation = { condition: "no_action", threshold_minutes: 120, action: "notify_manager", target: "" };

const RoutingRulesPage = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = ["admin", "ceo", "hod_sales"].includes(currentUser?.role);
  const [rules, setRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRule, setExpandedRule] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: "", description: "", priority_order: 100, active: true,
    conditions: [], conditions_logic: "AND",
    assignment_method: "round_robin", assign_team: "", assign_user: "",
    assign_stage: "new", assign_priority: "warm",
    immediate_actions: [], escalations: [],
  });

  const fetchData = async () => {
    try {
      const [rulesRes, teamsRes, usersRes] = await Promise.all([
        axios.get(`${API}/routing/rules`),
        axios.get(`${API}/teams`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/users`).catch(() => ({ data: [] })),
      ]);
      setRules(rulesRes.data);
      setTeams(teamsRes.data);
      setAllUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (err) {
      toast.error("Failed to load routing rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSeedDefaults = async () => {
    const ok = window.confirm(
      "This will create the 7 default source/project routing rules (MhM, CP Portal, Sampoorna Sales/Telecaller, Highpark Sales/Telecaller, Fallback). " +
      "If they already exist, they will be refreshed to defaults — any manual edits on those 7 rules will be overwritten. " +
      "Your own custom rules are NOT touched.\n\nProceed?"
    );
    if (!ok) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/routing/seed-defaults`);
      const { created, updated, teams_missing } = res.data || {};
      if (teams_missing?.length) {
        toast.error(`Missing teams: ${teams_missing.join(", ")}. Create them in /teams first.`);
      } else {
        toast.success(`Defaults seeded — ${created} created, ${updated} refreshed`);
        fetchData();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to seed defaults");
    } finally {
      setSeeding(false);
    }
  };

  const handleRerouteInactive = async () => {
    const ok = window.confirm(
      "Scan every lead currently assigned to a deactivated user and re-run the routing rules on them. " +
      "Leads that match a rule will be reassigned to the correct team; leads that don't match any rule are left alone.\n\nProceed?"
    );
    if (!ok) return;
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/routing/reroute-leads`, {
        only_inactive_assignees: true,
        limit: 5000,
      });
      const { scanned, rerouted, skipped_no_rule } = res.data || {};
      toast.success(`Scanned ${scanned} · rerouted ${rerouted} · no-rule ${skipped_no_rule}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reroute failed");
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditingRule(null);
    setForm({
      name: "", description: "", priority_order: 100, active: true,
      conditions: [{ ...emptyCondition }], conditions_logic: "AND",
      assignment_method: "round_robin", assign_team: "", assign_user: "",
      assign_stage: "new", assign_priority: "warm",
      immediate_actions: [], escalations: [],
    });
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name || "",
      description: rule.description || "",
      priority_order: rule.priority_order || 100,
      active: rule.active !== false,
      conditions: rule.conditions?.length > 0 ? rule.conditions : [{ ...emptyCondition }],
      conditions_logic: rule.conditions_logic || "AND",
      assignment_method: rule.assignment_method || "round_robin",
      assign_team: rule.assign_team || "",
      assign_user: rule.assign_user || "",
      assign_stage: rule.assign_stage || "new",
      assign_priority: rule.assign_priority || "warm",
      immediate_actions: rule.immediate_actions || [],
      escalations: rule.escalations || [],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Rule name is required"); return; }
    const payload = {
      ...form,
      conditions: form.conditions.filter(c => c.value !== ""),
      immediate_actions: form.immediate_actions.filter(a => a.value !== ""),
      assign_team: form.assign_team || null,
      assign_user: form.assign_user || null,
    };
    try {
      if (editingRule) {
        await axios.put(`${API}/routing/rules/${editingRule.rule_id}`, payload);
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/routing/rules`, payload);
        toast.success("Rule created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rule");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this routing rule?")) return;
    try {
      await axios.delete(`${API}/routing/rules/${id}`);
      toast.success("Rule deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleActive = async (rule) => {
    try {
      await axios.put(`${API}/routing/rules/${rule.rule_id}`, { active: !rule.active });
      fetchData();
    } catch (err) {
      toast.error("Failed to toggle status");
    }
  };

  const duplicateRule = (rule) => {
    setEditingRule(null);
    setForm({
      name: `${rule.name} (Copy)`,
      description: rule.description || "",
      priority_order: rule.priority_order + 1,
      active: false,
      conditions: rule.conditions || [{ ...emptyCondition }],
      conditions_logic: rule.conditions_logic || "AND",
      assignment_method: rule.assignment_method || "round_robin",
      assign_team: rule.assign_team || "",
      assign_user: rule.assign_user || "",
      assign_stage: rule.assign_stage || "new",
      assign_priority: rule.assign_priority || "warm",
      immediate_actions: rule.immediate_actions || [],
      escalations: rule.escalations || [],
    });
    setDialogOpen(true);
  };

  // Condition helpers
  const addCondition = () => setForm({ ...form, conditions: [...form.conditions, { ...emptyCondition }] });
  const removeCondition = (i) => setForm({ ...form, conditions: form.conditions.filter((_, idx) => idx !== i) });
  const updateCondition = (i, key, val) => {
    const updated = [...form.conditions];
    updated[i] = { ...updated[i], [key]: val };
    setForm({ ...form, conditions: updated });
  };

  // Action helpers
  const addAction = () => setForm({ ...form, immediate_actions: [...form.immediate_actions, { ...emptyAction }] });
  const removeAction = (i) => setForm({ ...form, immediate_actions: form.immediate_actions.filter((_, idx) => idx !== i) });
  const updateAction = (i, key, val) => {
    const updated = [...form.immediate_actions];
    updated[i] = { ...updated[i], [key]: val };
    setForm({ ...form, immediate_actions: updated });
  };

  // Escalation helpers
  const addEscalation = () => setForm({ ...form, escalations: [...form.escalations, { ...emptyEscalation }] });
  const removeEscalation = (i) => setForm({ ...form, escalations: form.escalations.filter((_, idx) => idx !== i) });
  const updateEscalation = (i, key, val) => {
    const updated = [...form.escalations];
    updated[i] = { ...updated[i], [key]: val };
    setForm({ ...form, escalations: updated });
  };

  const filtered = rules.filter(r => r.name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const getTeamName = (id) => teams.find(t => t.team_id === id)?.name || id || "—";
  const getUserName = (id) => allUsers.find(u => u.user_id === id)?.name || id || "—";
  const getMethodLabel = (m) => ASSIGNMENT_METHODS.find(a => a.value === m)?.label || m;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="routing-rules-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Routing Rules</h1>
            <p className="text-sm text-slate-500 mt-1">Define conditions and actions to auto-route incoming leads</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleRerouteInactive}
                disabled={seeding}
                data-testid="reroute-inactive-btn"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                title="Reroute leads currently assigned to deactivated users"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Reroute Orphaned Leads
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleSeedDefaults}
                disabled={seeding}
                data-testid="seed-defaults-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                title="Creates/refreshes the 7 default source × project routing rules"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {seeding ? "Working..." : "Seed Default Rules"}
              </Button>
            )}
            <Button onClick={openCreate} data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-2" /> New Rule
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Rules</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="rules-total">{rules.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Active</p>
            <p className="text-2xl font-bold text-emerald-600">{rules.filter(r => r.active).length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">With Escalations</p>
            <p className="text-2xl font-bold text-amber-600">{rules.filter(r => r.escalations?.length > 0).length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Assignment Methods</p>
            <p className="text-2xl font-bold text-blue-600">{new Set(rules.map(r => r.assignment_method)).size}</p>
          </CardContent></Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search rules..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10" data-testid="rules-search" />
        </div>

        {/* Rules List */}
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading rules...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-400">
              <GitBranch className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No routing rules found. Create your first rule to start auto-routing leads.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((rule, idx) => (
              <Card key={rule.rule_id} className={`transition-shadow ${!rule.active ? 'opacity-60' : 'hover:shadow-md'}`}
                data-testid={`rule-card-${rule.rule_id}`}>
                <CardContent className="p-0">
                  {/* Rule Header */}
                  <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedRule(expandedRule === rule.rule_id ? null : rule.rule_id)}>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-sm font-bold text-slate-600">
                      {rule.priority_order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{rule.name}</h3>
                        {rule.active
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Active</Badge>
                          : <Badge variant="outline" className="text-xs text-slate-400">Inactive</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Target className="w-3 h-3" />{rule.conditions?.length || 0} conditions ({rule.conditions_logic})</span>
                        <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" />{getMethodLabel(rule.assignment_method)}</span>
                        {rule.assign_team && <span>Team: {getTeamName(rule.assign_team)}</span>}
                        {rule.escalations?.length > 0 && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />{rule.escalations.length} escalations</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); duplicateRule(rule); }}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(rule); }} data-testid={`rule-edit-${rule.rule_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); toggleActive(rule); }}>
                        {rule.active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); handleDelete(rule.rule_id); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      {expandedRule === rule.rule_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedRule === rule.rule_id && (
                    <div className="border-t px-4 pb-4 pt-3 bg-slate-50/50 space-y-3">
                      {rule.description && <p className="text-sm text-slate-600">{rule.description}</p>}
                      <div className="grid md:grid-cols-3 gap-4">
                        {/* Conditions */}
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">WHEN ({rule.conditions_logic})</p>
                          {(rule.conditions || []).length === 0 ? (
                            <p className="text-xs text-slate-400">All leads (no conditions)</p>
                          ) : (
                            <div className="space-y-1">
                              {rule.conditions.map((c, i) => (
                                <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border">
                                  <span className="font-medium text-slate-700">{c.field}</span>{" "}
                                  <span className="text-blue-600">{c.operator}</span>{" "}
                                  <span className="text-slate-900 font-medium">{Array.isArray(c.value) ? c.value.join(", ") : String(c.value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Assignment */}
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">THEN</p>
                          <div className="space-y-1 text-xs">
                            <div className="bg-white rounded px-2 py-1.5 border">
                              Assign via <span className="font-medium text-blue-700">{getMethodLabel(rule.assignment_method)}</span>
                            </div>
                            {rule.assign_team && <div className="bg-white rounded px-2 py-1.5 border">Team: <span className="font-medium">{getTeamName(rule.assign_team)}</span></div>}
                            {rule.assign_user && <div className="bg-white rounded px-2 py-1.5 border">User: <span className="font-medium">{getUserName(rule.assign_user)}</span></div>}
                            <div className="bg-white rounded px-2 py-1.5 border">Stage: <span className="font-medium">{rule.assign_stage}</span> | Priority: <span className="font-medium">{rule.assign_priority}</span></div>
                          </div>
                        </div>
                        {/* Actions & Escalations */}
                        <div>
                          {rule.immediate_actions?.length > 0 && (
                            <>
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">IMMEDIATE ACTIONS</p>
                              <div className="space-y-1 mb-3">
                                {rule.immediate_actions.map((a, i) => (
                                  <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border">
                                    <Zap className="w-3 h-3 inline mr-1 text-amber-500" />
                                    {a.action_type}: {String(a.value)}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {rule.escalations?.length > 0 && (
                            <>
                              <p className="text-xs font-semibold text-amber-600 uppercase mb-2">ESCALATIONS</p>
                              <div className="space-y-1">
                                {rule.escalations.map((e, i) => (
                                  <div key={i} className="text-xs bg-amber-50 rounded px-2 py-1.5 border border-amber-200">
                                    <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                                    If {e.condition} for {e.threshold_minutes}min → {e.action}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Rule Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rule-dialog">
            <DialogHeader>
              <DialogTitle>{editingRule ? "Edit Routing Rule" : "New Routing Rule"}</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="basics" className="mt-2">
              <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1">
                <TabsTrigger value="basics" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Basics</TabsTrigger>
                <TabsTrigger value="conditions" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Conditions</TabsTrigger>
                <TabsTrigger value="assignment" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Assignment</TabsTrigger>
                <TabsTrigger value="actions" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">Actions</TabsTrigger>
              </TabsList>

              {/* Basics Tab */}
              <TabsContent value="basics" className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Rule Name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Meta Leads → Telecaller Team" data-testid="rule-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional description of what this rule does" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Priority Order</label>
                    <Input type="number" value={form.priority_order} onChange={(e) => setForm({ ...form, priority_order: parseInt(e.target.value) || 100 })}
                      data-testid="rule-priority-input" />
                    <p className="text-xs text-slate-400 mt-1">Lower = higher priority (1 is checked first)</p>
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                    <label className="text-sm text-slate-700">{form.active ? "Active" : "Inactive"}</label>
                  </div>
                </div>
              </TabsContent>

              {/* Conditions Tab */}
              <TabsContent value="conditions" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-slate-700">Match Logic:</label>
                    <Select value={form.conditions_logic} onValueChange={(v) => setForm({ ...form, conditions_logic: v })}>
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">AND</SelectItem>
                        <SelectItem value="OR">OR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={addCondition} data-testid="add-condition-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Condition
                  </Button>
                </div>
                {form.conditions.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No conditions = matches ALL leads</p>
                )}
                <div className="space-y-2">
                  {form.conditions.map((cond, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border" data-testid={`condition-row-${i}`}>
                      <Select value={cond.field} onValueChange={(v) => updateCondition(i, "field", v)}>
                        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CONDITION_FIELDS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={cond.operator} onValueChange={(v) => updateCondition(i, "operator", v)}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input className="flex-1" value={cond.value} onChange={(e) => updateCondition(i, "value", e.target.value)}
                        placeholder="Value" />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0" onClick={() => removeCondition(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Assignment Tab */}
              <TabsContent value="assignment" className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Assignment Method</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ASSIGNMENT_METHODS.map((m) => (
                      <button key={m.value}
                        className={`text-left p-3 rounded-lg border transition-colors ${form.assignment_method === m.value ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                        onClick={() => setForm({ ...form, assignment_method: m.value })} data-testid={`method-${m.value}`}>
                        <p className="text-sm font-medium text-slate-800">{m.label}</p>
                        <p className="text-xs text-slate-500">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Assign to Team</label>
                    <Select value={form.assign_team || "_none"} onValueChange={(v) => setForm({ ...form, assign_team: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="rule-team-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No team</SelectItem>
                        {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name} ({t.team_type})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Fixed User (if applicable)</label>
                    <Select value={form.assign_user || "_none"} onValueChange={(v) => setForm({ ...form, assign_user: v === "_none" ? "" : v })}>
                      <SelectTrigger data-testid="rule-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No fixed user</SelectItem>
                        {allUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Initial Stage</label>
                    <Select value={form.assign_stage} onValueChange={(v) => setForm({ ...form, assign_stage: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAGES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Lead Priority</label>
                    <Select value={form.assign_priority} onValueChange={(v) => setForm({ ...form, assign_priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ASSIGN_PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* Actions & Escalations Tab */}
              <TabsContent value="actions" className="space-y-6 mt-4">
                {/* Immediate Actions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-slate-700">Immediate Actions</label>
                    <Button variant="outline" size="sm" onClick={addAction} data-testid="add-action-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add Action
                    </Button>
                  </div>
                  {form.immediate_actions.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-3">No immediate actions configured</p>
                  )}
                  <div className="space-y-2">
                    {form.immediate_actions.map((act, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border">
                        <Select value={act.action_type} onValueChange={(v) => updateAction(i, "action_type", v)}>
                          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className="flex-1" value={act.value} onChange={(e) => updateAction(i, "value", e.target.value)}
                          placeholder="Value (e.g. task title, tag name)" />
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeAction(i)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Escalation Rules */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-amber-700">Escalation Rules</label>
                    <Button variant="outline" size="sm" onClick={addEscalation} data-testid="add-escalation-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add Escalation
                    </Button>
                  </div>
                  {form.escalations.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-3">No escalation rules configured</p>
                  )}
                  <div className="space-y-2">
                    {form.escalations.map((esc, i) => (
                      <div key={i} className="bg-amber-50 rounded-lg p-3 border border-amber-200 space-y-2" data-testid={`escalation-row-${i}`}>
                        <div className="flex items-center gap-2">
                          <Select value={esc.condition} onValueChange={(v) => updateEscalation(i, "condition", v)}>
                            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no_action">No Action</SelectItem>
                              <SelectItem value="no_update">No Update</SelectItem>
                              <SelectItem value="no_contact">No Contact</SelectItem>
                              <SelectItem value="score_increase">Score Increase</SelectItem>
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500">for</span>
                          <Input type="number" className="w-20" value={esc.threshold_minutes}
                            onChange={(e) => updateEscalation(i, "threshold_minutes", parseInt(e.target.value) || 0)} />
                          <span className="text-xs text-slate-500">mins →</span>
                          <Select value={esc.action} onValueChange={(v) => updateEscalation(i, "action", v)}>
                            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="reassign">Reassign</SelectItem>
                              <SelectItem value="notify_manager">Notify Manager</SelectItem>
                              <SelectItem value="move_to_queue">Move to Queue</SelectItem>
                              <SelectItem value="change_priority">Change Priority</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeEscalation(i)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="rule-save-btn">{editingRule ? "Update Rule" : "Create Rule"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RoutingRulesPage;
